import { graphql, useFragment } from "react-relay";

import type { useMCPEnabled_query$key } from "./__generated__/useMCPEnabled_query.graphql";

interface MCPAvailability {
  /** Whether this deployment serves a hosted MCP endpoint. */
  isEnabled: boolean;
  /** The endpoint to connect an MCP client to, or null when there is none. */
  url: string | null;
}

/**
 * Reports whether this deployment offers hosted MCP, and where it lives.
 *
 * The endpoint is the whole signal: `application.json` carries `mcp_url` only
 * where hosted MCP is actually running, so its presence is what enablement
 * means. It is written into the Relay store at bootstrap, from the same read
 * that decides whether Jun0 appears at all -- MCP rides in Jun0's block, so a
 * deployment without Jun0 has no MCP either, and the two cannot disagree.
 *
 * A fragment rather than a query of its own, so a page that already selects
 * from `Query` pays no extra round trip for the answer.
 */
export function useMCPEnabled(
  queryRef: useMCPEnabled_query$key,
): MCPAvailability {
  const data = useFragment(
    graphql`
      fragment useMCPEnabled_query on Query {
        UrlConfig {
          mcp
        }
      }
    `,
    queryRef,
  );

  const url = data.UrlConfig?.mcp ?? null;
  return { isEnabled: !!url, url };
}
